/**
 * Auth Service - Authentication API
 * @module services/authService
 */

import { apiClient } from './apiClient'
import type { Permission } from '@/utils/permissions'

export interface TenantInfo {
  id: string
  name: string
  slug: string
  plan?: string
  subscriptionStatus?: string
  trialEndsAt?: string | null
  currentPeriodEnd?: string | null
  isSystem?: boolean
  /** Import/export avanzado, IA, etc. */
  advancedConfig?: boolean
  aiAssistant?: boolean
  trialDaysLeft?: number | null
  effectiveLimits?: Record<string, number | null>
  hasStripeCustomer?: boolean
}

export interface User {
  id: string
  email: string
  name: string
  role: string
  permissions?: Permission[]
  tenant: TenantInfo
}

export interface AuthResponse {
  user: User
  token: string
  message: string
}

const TOKEN_KEY = 'neteasyftx_token'
const USER_KEY = 'neteasyftx_user'
const IMPERSONATE_KEY = 'neteasyftx_impersonate_slug'

export const authService = {
  getImpersonateSlug(): string | null {
    return typeof localStorage !== 'undefined' ? localStorage.getItem(IMPERSONATE_KEY) : null
  },

  setImpersonateSlug(slug: string | null): void {
    if (slug) {
      localStorage.setItem(IMPERSONATE_KEY, slug)
    } else {
      localStorage.removeItem(IMPERSONATE_KEY)
    }
  },

  isSuperAdmin(): boolean {
    return this.getUser()?.role === 'SUPER_ADMIN'
  },

  async register(data: {
    email: string
    password: string
    name: string
    tenantSlug: string
  }): Promise<AuthResponse> {
    const response = await apiClient.post<AuthResponse>('/api/auth/register', data)
    this.saveAuth(response.token, response.user)
    apiClient.setTenantSlug(response.user.tenant.slug)
    return response
  },

  async login(data: {
    email: string
    password: string
    tenantSlug: string
  }): Promise<AuthResponse> {
    const response = await apiClient.post<AuthResponse>('/api/auth/login', data)
    this.saveAuth(response.token, response.user)
    apiClient.setTenantSlug(response.user.tenant.slug)
    return response
  },

  async acceptInvite(data: { token: string; password: string; name: string }): Promise<AuthResponse> {
    const response = await apiClient.post<AuthResponse>('/api/auth/accept-invite', data)
    this.saveAuth(response.token, response.user)
    apiClient.setTenantSlug(response.user.tenant.slug)
    return response
  },

  async getCurrentUser(): Promise<User | null> {
    const token = this.getToken()
    if (!token) return null

    try {
      const response = await fetch(`${apiClient.baseUrl}/api/auth/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        this.logout()
        return null
      }

      const data = await response.json()
      this.saveUser(data.user)
      if (data.user?.tenant?.slug) {
        apiClient.setTenantSlug(data.user.tenant.slug)
      }
      return data.user
    } catch {
      this.logout()
      return null
    }
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    const token = this.getToken()
    if (!token) throw new Error('Not authenticated')

    const response = await fetch(`${apiClient.baseUrl}/api/auth/change-password`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ currentPassword, newPassword }),
    })

    if (!response.ok) {
      const data = await response.json()
      throw new Error(data.error || 'Failed to change password')
    }
  },

  logout(): void {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(USER_KEY)
    localStorage.removeItem(IMPERSONATE_KEY)
  },

  saveAuth(token: string, user: User): void {
    localStorage.setItem(TOKEN_KEY, token)
    localStorage.setItem(USER_KEY, JSON.stringify(user))
  },

  saveUser(user: User): void {
    localStorage.setItem(USER_KEY, JSON.stringify(user))
  },

  getToken(): string | null {
    return typeof localStorage !== 'undefined' ? localStorage.getItem(TOKEN_KEY) : null
  },

  getUser(): User | null {
    const userStr = typeof localStorage !== 'undefined' ? localStorage.getItem(USER_KEY) : null
    if (!userStr) return null
    try {
      return JSON.parse(userStr)
    } catch {
      return null
    }
  },

  isAuthenticated(): boolean {
    return !!this.getToken()
  },

  hasRole(minRole: 'VIEWER' | 'TECHNICIAN' | 'DESIGNER' | 'TENANT_ADMIN' | 'SUPER_ADMIN'): boolean {
    const user = this.getUser()
    if (!user) return false
    const order: Record<string, number> = {
      VIEWER: 1,
      TECHNICIAN: 2,
      DESIGNER: 3,
      EDITOR: 3,
      TENANT_ADMIN: 4,
      ADMIN: 4,
      SUPER_ADMIN: 5,
    }
    return (order[user.role] ?? 0) >= (order[minRole] ?? 0)
  },

  isTenantAdmin(): boolean {
    const r = this.getUser()?.role
    return r === 'TENANT_ADMIN' || r === 'ADMIN' || r === 'SUPER_ADMIN'
  },
}
